import { createFileRoute, Link } from "@tanstack/react-router";
import { SEOHead } from "@/components/shared/SEOHead";

export const Route = createFileRoute("/terms")({
  component: TermsPage,
});

function PageNav() {
  return (
    <nav aria-label="Related pages" className="flex flex-wrap gap-2 mb-10">
      <Link
        to="/privacy"
        className="px-4 py-1.5 rounded-full text-sm font-medium bg-qds-surface border border-qds-border text-qds-text-secondary hover:border-qds-accent/40 hover:text-qds-text-primary transition-colors min-h-[44px] inline-flex items-center"
      >
        Privacy Policy
      </Link>
      <Link
        to="/terms"
        className="px-4 py-1.5 rounded-full text-sm font-medium bg-qds-accent text-qds-text-inverse transition-colors min-h-[44px] inline-flex items-center"
        aria-current="page"
      >
        Terms of Service
      </Link>
      <Link
        to="/support"
        className="px-4 py-1.5 rounded-full text-sm font-medium bg-qds-surface border border-qds-border text-qds-text-secondary hover:border-qds-accent/40 hover:text-qds-text-primary transition-colors min-h-[44px] inline-flex items-center"
      >
        Support
      </Link>
    </nav>
  );
}

function TermsPage() {
  return (
    <div className="page-enter bp-utility-page bp-legal-page">
      <SEOHead
        title="Terms of Service"
        description="The terms that govern your use of Quuil. Straightforward and readable."
        path="/terms"
      />

      <div className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-20">
        <PageNav />

        <header className="mb-10">
          <h1 className="text-3xl md:text-4xl font-light tracking-tight text-qds-text-primary mb-3">
            Terms of Service
          </h1>
          <p className="text-sm text-qds-text-tertiary">
            Last updated: January 7, 2026
          </p>
        </header>

        <div className="space-y-10 text-qds-text-secondary text-[15px] leading-relaxed">
          {/* Acceptance */}
          <section>
            <h2 className="text-lg font-medium text-qds-text-primary mb-3">Acceptance of Terms</h2>
            <p>
              By accessing or using the Quuil application and related services (the "Service"),
              you agree to be bound by these Terms of Service ("Terms"). If you do not agree to
              these Terms, please do not use the Service.
            </p>
            <p className="mt-3">
              We may update these Terms from time to time. Continued use of the Service after
              changes are posted constitutes acceptance of the updated Terms.
            </p>
          </section>

          {/* Accounts */}
          <section>
            <h2 className="text-lg font-medium text-qds-text-primary mb-3">Accounts</h2>
            <p>
              You must create an account using Sign in with Apple to access certain features of
              the Service. You are responsible for maintaining the security of your account and
              for all activities that occur under your account.
            </p>
            <p className="mt-3">
              You must be at least 13 years of age to create an account. If you are under 18,
              you must have parental or guardian consent to use the Service.
            </p>
          </section>

          {/* Acceptable Use */}
          <section>
            <h2 className="text-lg font-medium text-qds-text-primary mb-3">Acceptable Use</h2>
            <p className="mb-3">You agree not to use the Service to:</p>
            <ul className="list-disc pl-5 space-y-2 text-sm">
              <li>Upload, create, or share content that is illegal, harmful, threatening, abusive, harassing, defamatory, or otherwise objectionable</li>
              <li>Infringe on the intellectual property rights of others</li>
              <li>Distribute malware, spam, or other harmful content</li>
              <li>Attempt to gain unauthorized access to the Service or its systems</li>
              <li>Use the Service for any commercial purpose not expressly permitted</li>
              <li>Impersonate any person or entity or misrepresent your affiliation</li>
              <li>Interfere with or disrupt the Service or servers connected to the Service</li>
              <li>Generate content depicting real individuals without their consent</li>
            </ul>
          </section>

          {/* Credits, Purchases, and Refunds */}
          <section>
            <h2 className="text-lg font-medium text-qds-text-primary mb-3">Credits, Purchases, and Refunds</h2>
            <div className="space-y-3">
              <p>
                Quuil uses a credit-based system. Credits can be purchased through Apple's
                in-app purchase system and are used to generate designs and access premium
                features.
              </p>
              <p>
                Credits are non-transferable and have no cash value. Unused credits remain
                in your account indefinitely unless your account is deleted.
              </p>
              <p>
                All purchases are processed by Apple and are subject to Apple's terms and
                refund policies. To request a refund, please contact Apple Support through
                your purchase history or the App Store.
              </p>
              <p>
                We reserve the right to modify credit pricing and the credit cost of features
                at any time, though changes will not affect credits you have already purchased.
              </p>
            </div>
          </section>

          {/* Your Content */}
          <section>
            <h2 className="text-lg font-medium text-qds-text-primary mb-3">Your Content</h2>
            <p>
              You retain ownership of content you create using the Service, including designs
              and uploaded images. By sharing content to the Inkwell community gallery, you
              grant Quuil a non-exclusive, worldwide, royalty-free license to display and
              distribute that content within the Service.
            </p>
            <p className="mt-3">
              You are responsible for ensuring you have the right to use any images you upload.
              We may remove content that violates these Terms or applicable law.
            </p>
          </section>

          {/* Service Availability */}
          <section>
            <h2 className="text-lg font-medium text-qds-text-primary mb-3">Service Availability</h2>
            <p>
              We strive to keep the Service available at all times, but we do not guarantee
              uninterrupted access. The Service may be temporarily unavailable due to maintenance,
              updates, or circumstances beyond our control. We will make reasonable efforts to
              provide advance notice of planned downtime.
            </p>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-lg font-medium text-qds-text-primary mb-3">Termination</h2>
            <p>
              You may delete your account at any time through the app's settings. We may suspend
              or terminate your account if you violate these Terms or engage in behavior that is
              harmful to the Service or other users.
            </p>
            <p className="mt-3">
              Upon termination, your right to use the Service ceases immediately. We will delete
              your personal data in accordance with our{" "}
              <Link to="/privacy" className="text-qds-accent hover:text-qds-accent-hover font-medium underline underline-offset-2 min-h-auto">
                Privacy Policy
              </Link>.
            </p>
          </section>

          {/* Disclaimers */}
          <section>
            <h2 className="text-lg font-medium text-qds-text-primary mb-3">Disclaimers</h2>
            <div className="bg-qds-surface rounded-xl p-5 border border-qds-border text-sm space-y-3">
              <p>
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF
                MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
              <p>
                WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE,
                OR THAT DEFECTS WILL BE CORRECTED.
              </p>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, QUUIL SHALL NOT BE LIABLE FOR ANY INDIRECT,
                INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF YOUR USE OF
                THE SERVICE.
              </p>
            </div>
          </section>

          {/* Contact */}
          <section className="bg-qds-accent-tint rounded-xl p-6 border border-qds-accent/15">
            <h2 className="text-lg font-medium text-qds-text-primary mb-2">Questions?</h2>
            <p className="text-sm">
              Something here not making sense? Reach out at{" "}
              <a
                href="mailto:support@quuil.com"
                className="text-qds-accent hover:text-qds-accent-hover font-medium underline underline-offset-2 min-h-auto"
              >
                support@quuil.com
              </a>
              . We're happy to clarify.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
